import React, { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import * as AIPageDesignAPI from '../api/AIPageDesign';
import { AIPageDesign } from '../api/AIPageDesign';

const AIPageDesignManagement = () => {
  usePageTitle('AI Page Design Management');

  const [designs, setDesigns] = useState<AIPageDesign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    aiPrompt: '',
    type: [] as string[],
    note: '',
    fileLink: ''
  });

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');
  const [generatedExplanation, setGeneratedExplanation] = useState('');
  const [generatedSuggestions, setGeneratedSuggestions] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [selectedModel, setSelectedModel] = useState<'gpt-4' | 'gpt-4-turbo' | 'gpt-3.5-turbo'>('gpt-4');
  const [showCodePreview, setShowCodePreview] = useState(false);
  const [currentDesignId, setCurrentDesignId] = useState<string | null>(null);

  // Page types
  const pageTypes = ['Landing', 'Marketing', 'Dashboard', 'About', 'Contact', 'Features', 'Pricing', 'FAQ', 'Other'];

  useEffect(() => {
    fetchDesigns();
  }, []);

  const fetchDesigns = async () => {
    try {
      setLoading(true);
      const data = await AIPageDesignAPI.getAllDesigns();
      setDesigns(data);
      setError('');
    } catch (err: any) {
      setError('Failed to load page designs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingId) {
        await AIPageDesignAPI.updateDesign(editingId, formData);
        setSuccess('Page design updated successfully');
      } else {
        await AIPageDesignAPI.createDesign(formData);
        setSuccess('Page design created successfully');
      }
      
      await fetchDesigns();
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save page design');
    }
  };

  const handleEdit = (design: AIPageDesign) => {
    setEditingId(design._id);
    setFormData({
      aiPrompt: design.aiPrompt || '',
      type: design.type || [],
      note: design.note || '',
      fileLink: design.fileLink || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this page design?')) {
      return;
    }

    try {
      await AIPageDesignAPI.deleteDesign(id);
      setSuccess('Page design deleted successfully');
      await fetchDesigns();
    } catch (err: any) {
      setError('Failed to delete page design');
    }
  };

  const handleGenerate = async (designId: string) => {
    console.log('🤖 Generate button clicked!');
    console.log('Design ID:', designId);
    console.log('Selected Model:', selectedModel);
    
    setGenerating(true);
    setError('');
    setCurrentDesignId(designId);

    try {
      console.log('📡 Calling API to generate page code...');
      const result = await AIPageDesignAPI.generatePageCode(designId, { model: selectedModel });
      console.log('✅ API Response received:', result);
      
      // Store all the response data
      setGeneratedCode(result.result.code);
      setGeneratedExplanation(result.result.explanation);
      setGeneratedSuggestions(result.result.suggestions);
      setValidationErrors(result.validation.errors);
      setShowCodePreview(true);
      setSuccess(`Page generated successfully using ${result.design.aiModel}!`);
      
      if (!result.validation.isValid) {
        setError(`Warning: ${result.validation.errors.join(', ')}`);
      }

      await fetchDesigns();
    } catch (err: any) {
      console.error('❌ Error generating page:', err);
      setError(err.response?.data?.message || 'Failed to generate page');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveToFile = async () => {
    if (!currentDesignId || !generatedCode) return;

    try {
      // Use the design ID as the filename for consistency
      const design = designs.find(d => d._id === currentDesignId);
      if (!design) {
        setError('Design not found');
        return;
      }

      // Auto-generate filename from design ID: AIPage_[shortId]
      const shortId = currentDesignId.slice(-8);
      const fileName = `AIPage_${shortId}`;

      // Call backend to save file
      const result = await AIPageDesignAPI.saveToFile(currentDesignId, { fileName });
      
      // If code is returned, file wasn't saved (production mode) - offer download
      if (result.code) {
        const blob = new Blob([result.code], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileName}.tsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        setSuccess(`⬇️ File downloaded! ${result.note || 'Add route to App.tsx'}`);
      } else {
        setSuccess(`✅ File saved! Route: ${result.route} - Add to App.tsx and restart dev server.`);
      }
      
      setShowCodePreview(false);
      setGeneratedCode('');
      setGeneratedExplanation('');
      setGeneratedSuggestions([]);
      setValidationErrors([]);
      setCurrentDesignId(null);
      await fetchDesigns();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save file');
    }
  };

  const resetForm = () => {
    setFormData({ aiPrompt: '', type: [], note: '', fileLink: '' });
    setEditingId(null);
    setShowForm(false);
  };

  // Convert file path to route
  const getPageRoute = (fileLink: string): string | null => {
    if (!fileLink) return null;
    
    // Extract filename from path: /src/pages/pagedesign/LandingModern.tsx -> LandingModern
    const match = fileLink.match(/\/([^/]+)\.tsx$/);
    if (!match) return null;
    
    const fileName = match[1];
    
    // Convert PascalCase to kebab-case: LandingModern -> landing-modern
    const route = fileName
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .toLowerCase();
    
    return `/${route}`;
  };

  const handleViewPage = (fileLink: string) => {
    const route = getPageRoute(fileLink);
    if (route) {
      window.open(route, '_blank');
    } else {
      setError('Cannot determine page route from file link');
    }
  };

  const toggleType = (type: string) => {
    setFormData(prev => ({
      ...prev,
      type: prev.type.includes(type)
        ? prev.type.filter(t => t !== type)
        : [...prev.type, type]
    }));
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: 'bg-secondary',
      generated: 'bg-info',
      deployed: 'bg-success',
      archived: 'bg-dark'
    };
    return badges[status as keyof typeof badges] || 'bg-secondary';
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>AI Page Design Management</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : 'New Design Request'}
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">{editingId ? 'Edit' : 'Create'} Page Design Request</h5>
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">AI Prompt *</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={formData.aiPrompt}
                  onChange={(e) => setFormData({ ...formData, aiPrompt: e.target.value })}
                  placeholder="Describe the page you want to create... (e.g., 'Create a modern landing page with a hero section, features grid, and call-to-action buttons')"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Page Types</label>
                <div className="d-flex flex-wrap gap-2">
                  {pageTypes.map(type => (
                    <button
                      key={type}
                      type="button"
                      className={`btn btn-sm ${formData.type.includes(type) ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => toggleType(type)}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Additional Notes</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  placeholder="Any additional context or requirements..."
                />
              </div>

              <div className="mb-3">
                <label className="form-label">File Link (optional)</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.fileLink}
                  onChange={(e) => setFormData({ ...formData, fileLink: e.target.value })}
                  placeholder="/src/pages/pagedesign/MyPage.tsx"
                />
              </div>

              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary">
                  {editingId ? 'Update' : 'Create'} Design Request
                </button>
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Code Preview Modal */}
      {showCodePreview && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🤖 AI Generated Code Preview</h5>
                <button type="button" className="btn-close" onClick={() => setShowCodePreview(false)}></button>
              </div>
              <div className="modal-body">
                {/* Explanation Section */}
                {generatedExplanation && (
                  <div className="alert alert-info mb-3">
                    <h6 className="alert-heading">📝 What was generated:</h6>
                    <p className="mb-0">{generatedExplanation}</p>
                  </div>
                )}

                {/* Validation Warnings */}
                {validationErrors.length > 0 && (
                  <div className="alert alert-warning mb-3">
                    <h6 className="alert-heading">⚠️ Validation Warnings:</h6>
                    <ul className="mb-0">
                      {validationErrors.map((error, i) => (
                        <li key={i}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Suggestions */}
                {generatedSuggestions.length > 0 && (
                  <div className="alert alert-success mb-3">
                    <h6 className="alert-heading">💡 Recommendations:</h6>
                    <ul className="mb-0">
                      {generatedSuggestions.map((suggestion, i) => (
                        <li key={i}>{suggestion}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Code Display */}
                <h6 className="mb-2">Generated TypeScript Code:</h6>
                <pre className="bg-dark text-light p-3 rounded" style={{ maxHeight: '50vh', overflow: 'auto' }}>
                  <code>{generatedCode}</code>
                </pre>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowCodePreview(false)}>
                  Close
                </button>
                <button className="btn btn-primary" onClick={handleSaveToFile}>
                  Save to File
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Designs List */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Page Design Requests ({designs.length})</h5>
          <div className="d-flex align-items-center gap-2">
            <label className="mb-0 me-2">AI Model:</label>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value as any)}
            >
              <option value="gpt-4">GPT-4</option>
              <option value="gpt-4-turbo">GPT-4 Turbo</option>
              <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
            </select>
          </div>
        </div>
        <div className="card-body p-0">
          {designs.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <p>No page designs yet. Create one to get started!</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Prompt</th>
                    <th>Types</th>
                    <th>Status</th>
                    <th>Model</th>
                    <th>File</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {designs.map(design => (
                    <tr key={design._id}>
                      <td className="text-nowrap">
                        {new Date(design.creationDate).toLocaleDateString()}
                      </td>
                      <td>
                        <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {design.aiPrompt || '-'}
                        </div>
                      </td>
                      <td>
                        {design.type && design.type.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {design.type.map((t, i) => (
                              <span key={i} className="badge bg-secondary">{t}</span>
                            ))}
                          </div>
                        ) : '-'}
                      </td>
                      <td>
                        <span className={`badge ${getStatusBadge(design.status)}`}>
                          {design.status}
                        </span>
                      </td>
                      <td className="text-nowrap">{design.aiModel || '-'}</td>
                      <td>
                        {design.fileLink ? (
                          <a href="#" className="text-decoration-none" title={design.fileLink}>
                            📄
                          </a>
                        ) : '-'}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleGenerate(design._id)}
                            disabled={generating || !design.aiPrompt}
                            title="Generate with AI"
                          >
                            {generating ? '⏳' : '🤖'}
                          </button>
                          {design.generatedCode && (
                            <button
                              className="btn btn-sm btn-info"
                              onClick={() => {
                                setGeneratedCode(design.generatedCode!);
                                setCurrentDesignId(design._id);
                                setShowCodePreview(true);
                              }}
                              title="View Code"
                            >
                              👁️
                            </button>
                          )}
                          {design.fileLink && (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleViewPage(design.fileLink!)}
                              title="View Page in New Tab"
                            >
                              🌐
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={() => handleEdit(design)}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(design._id)}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIPageDesignManagement;
